'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Bell } from 'lucide-react';
import { apiFetch } from '@/lib/api';

type Notification = {
  id: number;
  type: string;
  actorStageName?: string | null;
  actorPersonId?: number | null;
  message?: string | null;
  createdAt: string;
  isRead: boolean;
  link?: string | null;
  // NotificationsController returns the stored deep link as `url`
  url?: string | null;
};

type NotificationsResponse = {
  items?: Notification[];
  notifications?: Notification[];
} | Notification[];

function formatRelative(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

function describeNotification(n: Notification): string {
  if (n.message) return n.message;
  const actor = n.actorStageName || 'Someone';
  switch (n.type) {
    case 'new_follower':
      return `${actor} started following you`;
    case 'profile_view':
      return `${actor} viewed your profile`;
    case 'new_comment':
      return `${actor} commented on your content`;
    case 'badge_earned':
      return 'You earned a new badge';
    default:
      return `${actor}: ${n.type.replace(/_/g, ' ')}`;
  }
}

export default function RecentActivityFeed() {
  const [items, setItems] = useState<Notification[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/proxy/api/notifications?limit=20', undefined, { method: 'GET' });
        if (!res.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data: NotificationsResponse = await res.json();
        const list = Array.isArray(data) ? data : (data.items || data.notifications || []);
        if (!cancelled) setItems(list);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-serif text-lg font-semibold">Recent activity</h2>
        <span className="juicy-rule ml-1" aria-hidden="true" />
      </div>

      {items === null && !error ? (
        <ul className="space-y-2">
          {[1, 2, 3].map((i) => (
            <li key={i} className="rounded-lg border border-border bg-card p-3">
              <div className="h-4 w-2/3 bg-muted/60 rounded animate-pulse" />
            </li>
          ))}
        </ul>
      ) : error ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          We couldn&apos;t load activity right now. Try again later.
        </div>
      ) : items && items.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground flex items-center gap-2">
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          No activity yet. Follow other creators to start filling this feed.
        </div>
      ) : (
        <ul className="space-y-2">
          {items!.slice(0, 20).map((n) => {
            const desc = describeNotification(n);
            const href = n.link || n.url
              || (n.actorStageName ? `/u/${encodeURIComponent(n.actorStageName)}` : '/notifications');
            return (
              <li key={n.id}>
                <Link
                  href={href}
                  className="block rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className={'text-sm truncate ' + (n.isRead ? 'text-muted-foreground' : 'font-medium')}>
                      {desc}
                    </span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatRelative(n.createdAt)}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
